import React, { Component } from 'react';
import {
    MediaPlayer as MLBMediaPlayer,
    MediaItem as MLBMediaItem,
    MediaConfigUtil as MLBMediaConfigUtil,
    MediaPlayerEvents,
} from 'mlb-media-player';
import _get from 'lodash/get';
import _set from 'lodash/set';
import _assign from 'lodash/assign';
import PropTypes from 'prop-types';
import './media-player.scss';
import MediaItem from '../../models/media-item';
import AdContainer from '..//ad-container/ad-container';
import Overlay from '../overlay/overlay';

class MediaPlayer extends Component {

    /**
     *
     * @param {object} props
     * @consructor
     *
     */
    constructor(props) {

        super(props);

        /**
         *
         * @type {MLBPlayerPlugins.BandwagonAdapter|MediaPlayer.AbstractPlugin}
         *
         */
        this.bandwagonAdapter = undefined;

        /**
         *
         * @type {boolean}
         *
         */
        this.playerIsReady = undefined;

        this.shouldRequestPreroll = true;

        this.handleOverlayClick = this.handleOverlayClick.bind(this);

        this.bandwagonAdapter - undefined;

        this.advertisingPlugin = undefined;

        this.adBlockedError = false;

        this.apmTransaction = null;

        this.apmSpan = null;

        this.state = {
            showOverlay: !this.props.autoplay,
            forcePlaybackAfterUpdate: !this.props.autoplay,
        };

    }

    componentDidMount() {

        const {
            _apm,
            appName,
            serviceUrl,
        } = this.props.webMediaApm;

        this.createPlayer();

        if (_apm.getCurrentTransaction() === undefined) {

            this.apmTransaction = _apm.startTransaction('Player Load', 'media-metrics');

        }

        this.apmSpan = _apm.startSpan('Player Load', 'custom');

    }

    shouldComponentUpdate(nextProps, nextState) {

        const srcPath = 'clip.src';

        const isStateUpdated = _get(this.state, 'showOverlay') !== _get(this.nextState, 'showOverlay');

        const isPropsUpdated = _get(this.props, srcPath) !== _get(nextProps, srcPath);


        return isStateUpdated || isPropsUpdated;

    }

    componentDidUpdate(prevProps) {

        // Update the player source if there is a playlist
        const { clip } = this.props;
        const idPath = 'clip.contentId';
        const isMediaUpdated = _get(this.props, idPath) != _get(prevProps, idPath);

        if (this.playerIsReady && isMediaUpdated) {

            this.requestAdOrUpdateSource(clip);

            this.chartBeatPlugin.setMetadata(clip);
            this.convivaPlugin.setTrackingMetadata(generateConvivaAssetName(clip), generateConvivaViewerId());
            this.bandwagonAdapter.publishMessage('META_DATA_LOADED', clip);

        }

    }

    requestAdOrUpdateSource(clip) {

        if (this.shouldRequestPreroll && this.advertisingPlugin && !this.adBlockedError) {

            this.advertisingPlugin.requestAd(clip);

        } else {

            this.updatePlayerSource();

        }

    }

    /**
     *
     * Determine if the user is a EU user
     *
     */
    determineEu() {

        return fetch('https://terra.mlb.com')
            .then((res) => res.json())
            .then((data) => {

                return data.isEuropeanUnion;

            });

    }

    setConvivaData(metaData) {

        const pluginManager = this.mediaPlayer.pluginManager;
        this.convivaPlugin = pluginManager.getPlugin('ConvivaPlugin');

        if (this.convivaPlugin) {

            // Set metaData in the video dom element
            this.convivaPlugin.setTrackingMetadata(generateConvivaAssetName(metaData), generateConvivaViewerId());

        }

        // Update chartbeat too...if it exists
        if (this.chartBeatPlugin) {

            this.chartBeatPlugin.setMetadata(metaData);

        }

    }

    /**
     *
     * @desc updates the player src based on props
     *
     */
    updatePlayerSource() {

        const { clip } = this.props;
        const metaData = _get(clip, 'data');

        const mediaItemObj = {
            poster: _get(clip, 'poster.src'),
            src: _get(clip, 'src'),
            metaData,
            vttItems: _get(clip, 'vttItems'),
        };

        const mediaItem = new MLBMediaItem(mediaItemObj);

        return this.mediaPlayer.setMediaItem(mediaItem)
            .then(() => {

                this.setConvivaData(metaData);

                if(this.state.forcePlaybackAfterUpdate) {

                    this.mediaPlayer.play();
                    this.setState({
                        forcePlaybackAfterUpdate: false,
                    });

                }

            })
            .catch((error) => {

                console.error(error);

            });

    }

    getMediaPlayerConfig() {

        const {
            mediaConfig,
            clip,
            preload,
            autoplay,
            mute,
            loop,
            width,
            height,
            deviceInfo,
        } = this.props;

        const isMobile = [
            'tablet',
            'phone',
        ].indexOf(deviceInfo.formFactor) > -1 ? true : false;

        let config = {};

        _assign(config, mediaConfig, {
            playerTypeConfig: {
                ...mediaConfig.playerTypeConfig,
                description: _get(clip, 'description'),
                subtitle: _get(clip, 'subtitle'),  // Not sure if this is needed for the app, it's not for the player
                thumb: _get(clip, 'image.src'), // Not sure if this is needed for the app, it's not for the player
                title: _get(clip, 'title'), // Not sure if this is needed for the app, it's not for the player
                controls: isMobile,
                preload,
                autoplay,
                mute,
                loop,
                width,
                height,
                deviceInfo,
            },
        });

        return MLBMediaConfigUtil.create(config);

    }

    getAdPlugin(pluginManager, mediaConfig) {

        const isFreewheelSite = _get(mediaConfig, 'videocore.advertising.freewheel.isFreeWheelSite');
        const pluginType = isFreewheelSite ? 'FreewheelPlugin' : 'DfpPlugin';
        const adPlugin = pluginManager.getPlugin(pluginType);

        if (!adPlugin) {

            console.error(`No plugin found for type ${pluginType}!`);

        }

        return adPlugin;

    }

    /**
     *
     * @desc Creates the media player
     * @todo update how we're getting the initial media metadata
     *
     */
    createPlayer() {

        let pluginManager;
        const metaData = _get(this.props, 'clip.data');

        this.determineEu()
            .then((isEu) => {

                const { mediaPlayerReady } = this.props;

                _set(this.props.mediaConfig, 'videocore.advertising.dfp.isEu', isEu);

                this.config = this.getMediaPlayerConfig();
                this.mediaPlayer = new MLBMediaPlayer(this.config);
                this.observationCenter = this.mediaPlayer.observationCenter;
                this.subscribeToPlayerSubject();
                this.subscribeToPlugInSubject();

                if (mediaPlayerReady) {

                    mediaPlayerReady(this.mediaPlayer);

                }

                return this.mediaPlayer.initPlayer();

            })
            .then((result) => {

                const {
                    mediaConfig,
                    clip,
                } = this.props;

                // Get the pluginManager and bandwagonAdapter
                pluginManager = this.mediaPlayer.pluginManager;

                this.bandwagonAdapter = pluginManager.getPlugin('BandwagonAdapter');

                this.advertisingPlugin = this.getAdPlugin(pluginManager, mediaConfig);

                // Get Analytics Plugins
                this.chartBeatPlugin = pluginManager.getPlugin('ChartBeatPlugin');

                // let the parent frame know about the metadata
                if (this.bandwagonAdapter) {

                    this.bandwagonAdapter.publishMessage('META_DATA_LOADED', metaData);

                }

                this.playerIsReady = true;

                if(!this.state.showOverlay) {

                    this.requestAdOrUpdateSource(clip);

                }

            });

    }

    subscribeToPlayerSubject() {

        const HTML5PlayerEvents = MediaPlayerEvents.HTML5;

        const { clip } = this.props;

        this.observationCenter.playerSubject
            .subscribe((state) => {

                switch (state.type) {

                    case HTML5PlayerEvents.MEDIA_COMPLETE: {

                        this.shouldRequestPreroll = !this.adBlockedError;
                        this.props.onComplete();
                        break;

                    }

                    case HTML5PlayerEvents.MEDIA_LOADED_META: {

                        if (this.apmSpan) {
                            
                            this.apmSpan.end();

                        }
                        break;

                    }

                    case HTML5PlayerEvents.MEDIA_REPLAY: {

                        this.shouldRequestPreroll = false;
                        break;

                    }

                }


            });

    }

    subscribeToPlugInSubject() {

        var autoplay = _get(this.config, 'playerTypeConfig.autoPlay');

        this.observationCenter.pluginSubject
            .subscribe((state) => {

                if(!state) return;

                switch(state.type) {

                    case 'adLoaded': {

                        //this can be done later when we allow the access to the requestAd function of the plug-in?
                        //this.requestAd(this.videoAdNode);
                        break;

                    }
                    case 'adRequestStarted':
                    case 'adStart': {

                        this.mediaPlayer.pause();
                        this.shouldRequestPreroll = false;
                        break;

                    }
                    case 'adRequestComplete': {

                        this.shouldRequestPreroll = false;
                        if(state.slots.length === 0) {

                            this.updatePlayerSource();

                        }
                        break;

                    }
                    case 'adComplete': {

                        this.shouldRequestPreroll = false;
                        this.updatePlayerSource();
                        break;

                    }
                    case 'adError':
                    case 'adPluginError':
                    case 'adBlockDetected': {

                        this.adBlockedError = true;
                        this.shouldRequestPreroll = false;

                        /**
                         * Only call updatePlayerSource() if the player is ready
                         * Like when an ad plugin is enabled after the first play-though
                         */
                        if (this.playerIsReady) {

                            this.updatePlayerSource();

                        }

                        break;

                    }

                }

            });

    }

    replay() {

        this.mediaPlayer.replay();

    }

    handleOverlayClick() {

        this.requestAdOrUpdateSource(this.props.clip);
        this.setState({
            showOverlay: false,
        });

    }

    render() {

        return (
            <div id="media-player" className="media-player" tabIndex="0" role="group" aria-label="MLB Media Player">
                <AdContainer/>
                {this.state.showOverlay
                    ? <Overlay
                        onClick={this.handleOverlayClick}
                        poster={_get(this.props, 'clip.poster.at3x', _get(this.props, 'clip.poster.src', ''))}
                        title={_get(this.props, 'clip.title')}
                    />
                    : null
                }
            </div>
        );

    }

}

var generateConvivaAssetName = (metadata) => {

    let assetName = '{date} - {title} | {id}';
    for (let contentMetaItemKey in metadata) {

        let contentMetaItemValue = metadata[contentMetaItemKey];

        if(contentMetaItemKey === 'date') {

            contentMetaItemValue = new Date(contentMetaItemValue);
            var contentMetaItemValueYear = contentMetaItemValue.getUTCFullYear();
            var contentMetaItemValueMonth = (contentMetaItemValue.getUTCMonth() + 1).toLocaleString('en-US', {
                minimumIntegerDigits: 2,
                useGrouping: false,
            });
            var contentMetaItemValueDate = contentMetaItemValue.getUTCDate().toLocaleString('en-US', {
                minimumIntegerDigits: 2,
                useGrouping: false,
            });
            contentMetaItemValue = (contentMetaItemValueYear + '-' + contentMetaItemValueMonth + '-' + contentMetaItemValueDate);
            assetName = assetName.replace(new RegExp('\{' + contentMetaItemKey  + '\}', 'g'), contentMetaItemValue);

        } else {

            assetName = assetName.replace(new RegExp('\{' + contentMetaItemKey  + '\}', 'g'), contentMetaItemValue);

        }

    }

    return assetName;

};

var generateConvivaViewerId = () => {

    let mlbOktaToken = (window.mlbOkta && window.mlbOkta.getToken('id_token'));
    return (mlbOktaToken && mlbOktaToken.clientId);

};

MediaPlayer.propTypes = {
    onComplete: PropTypes.func,
    clip: PropTypes.instanceOf(MediaItem),
    autoplay: PropTypes.bool,
    mediaConfig: PropTypes.object,
    mediaPlayerReady: PropTypes.func,
    deviceInfo: PropTypes.object,
};

MediaPlayer.defaultProps = {
    onComplete: () => null,
    clip: null,
    autoplay: false,
    preload: true,
    mute: false,
    loop: false,
    width: null,
    height: null,
    mediaPlayerReady: undefined,
    deviceInfo: undefined,
};

export default MediaPlayer;
